/**
 * @fileoverview Text field input component with built-in clear functionality
 * @module ctl-text-field
 * @version 1.1.0
 * 
 * @description
 * A styled text input field component that wraps a native HTML input element.
 * Provides consistent styling, support for various input types (text, email, password, etc.),
 * optional clear button, prefix/suffix slots for icons or text, and full accessibility support.
 * 
 * Also supports multiline mode (textarea) via the `multiline` attribute or `type="textarea"`.
 * 
 * This component automatically syncs all relevant attributes to the internal input element
 * and provides a consistent visual design across your application. It's designed to be used
 * standalone or wrapped in a ctl-form-field for complete form field functionality.
 * 
 * @example
 * <!-- Basic text input -->
 * <ctl-text-field 
 *   type="text" 
 *   placeholder="Enter your name">
 * </ctl-text-field>
 * 
 * @example
 * <!-- Email input with clear button -->
 * <ctl-text-field 
 *   type="email" 
 *   placeholder="email@example.com"
 *   clearable>
 * </ctl-text-field>
 * 
 * @example
 * <!-- Multiline textarea -->
 * <ctl-text-field 
 *   multiline 
 *   rows="4"
 *   placeholder="Enter your message">
 * </ctl-text-field>
 * 
 * @example
 * <!-- Input with prefix icon -->
 * <ctl-text-field placeholder="Search">
 *   <ctl-icon slot="prefix" name="search"></ctl-icon>
 * </ctl-text-field>
 * 
 * @example
 * <!-- Password input with visibility toggle -->
 * <ctl-text-field type="password" placeholder="Password">
 *   <button slot="suffix" type="button">Show</button>
 * </ctl-text-field>
 * 
 * @cssprop --ctl-field-height - Field height (from token system)
 * @cssprop --ctl-input-padding-x - Horizontal padding (from token system)
 * @cssprop --ctl-radius-md - Border radius (from token system)
 * @cssprop --ctl-space-2 - Gap between elements (from token system)
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    --ctl-font: system-ui,-apple-system,Segoe UI,Roboto,Arial;

    display:block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .wrap{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    height: var(--ctl-field-height);
    padding: 0 var(--ctl-input-padding-x);
    border-radius: var(--ctl-radius-md);
    border: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    box-sizing: border-box;
    transition: border-color var(--ctl-transition-normal),
                box-shadow var(--ctl-transition-normal),
                background var(--ctl-transition-normal),
                opacity var(--ctl-transition-normal);
  }

  /* Multiline mode: auto height, top-aligned content */
  :host([multiline]) .wrap,
  :host([type="textarea"]) .wrap{
    height: auto;
    align-items: flex-start;
    padding-top: var(--ctl-input-padding-y, var(--ctl-space-2));
    padding-bottom: var(--ctl-input-padding-y, var(--ctl-space-2));
  }

  :host([invalid]) .wrap{
    border-color: var(--ctl-color-danger);
  }

  :host(:focus-within) .wrap{
    border-color: var(--ctl-color-focus);
    box-shadow: var(--ctl-shadow-focus);
  }

  :host([disabled]) .wrap{
    opacity: var(--ctl-opacity-disabled);
    background: color-mix(in srgb, var(--ctl-color-text) 2%, var(--ctl-color-surface));
    cursor: not-allowed;
  }

  .slot{
    display:inline-flex;
    align-items:center;
    color: var(--ctl-color-muted);
  }

  .slot:empty{ display:none; }

  input{
    all: unset;
    box-sizing: border-box;
    flex: 1;
    min-width: 0;
    font: inherit;
    color: inherit;
    line-height: 1.2;
  }

  input::placeholder{
    color: var(--ctl-color-muted);
  }

  textarea{
    all: unset;
    box-sizing: border-box;
    flex: 1;
    min-width: 0;
    font: inherit;
    color: inherit;
    line-height: 1.5;
    resize: vertical;
    width: 100%;
  }

  textarea::placeholder{
    color: var(--ctl-color-muted);
  }

  .btn{
    all: unset;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    width: var(--ctl-size-icon-btn);
    height: var(--ctl-size-icon-btn);
    border-radius: var(--ctl-radius-md);
    cursor: pointer;
    color: var(--ctl-color-muted);
    transition: background var(--ctl-transition-fast),
                color var(--ctl-transition-fast);
  }

  :host([multiline]) .btn,
  :host([type="textarea"]) .btn{
    align-self: flex-start;
  }

  .btn:hover{
    background: color-mix(in srgb, var(--ctl-color-text) 6%, transparent);
    color: var(--ctl-color-text);
  }
  
  .btn:focus-visible{
    outline: var(--ctl-focus-ring-width) solid var(--ctl-color-focus);
    outline-offset: var(--ctl-focus-ring-offset);
  }

  :host([disabled]) .btn{
    pointer-events:none;
  }

  .clear{ display:none; }
  :host([clearable]) .clear{ display:inline-flex; }

  /* Only show clear button when there is content and it's not readonly/disabled */
  :host([_hasvalue]) .clear{ display:inline-flex; }
  :host(:not([_hasvalue])) .clear{ display:none; }
  :host([readonly]) .clear{ display:none; }
  :host([disabled]) .clear{ display:none; }
</style>

<div class="wrap" part="wrap">
  <span class="slot" part="prefix"><slot name="prefix"></slot></span>

  <input id="input" part="input" />
  <textarea id="textarea" part="input" hidden></textarea>

  <button class="btn clear" id="clearBtn" type="button" aria-label="Clear" title="Clear" part="clear-button">
    ×
  </button>

  <span class="slot" part="suffix"><slot name="suffix"></slot></span>
</div>
`;

const REFLECT_TO_INPUT = new Set([
  // common
  "name", "placeholder", "autocomplete", "inputmode", "pattern",
  "maxlength", "minlength", "min", "max", "step",
  // a11y passthrough
  "aria-label", "aria-labelledby", "aria-describedby", "aria-invalid"
]);

function toAttr(v) {
  if (v === null || v === undefined) return null;
  return String(v);
}

/**
 * Text Field Web Component
 * 
 * A styled text input component that wraps a native input element with
 * consistent styling, clear functionality, and prefix/suffix slots.
 * 
 * @class CtlTextField
 * @extends HTMLElement
 * 
 * @attr {string} name - Input name for form submission
 * @attr {string} value - Current input value
 * @attr {string} placeholder - Placeholder text
 * @attr {boolean} disabled - Disables the input
 * @attr {boolean} required - Marks input as required
 * @attr {boolean} readonly - Makes input read-only
 * @attr {boolean} invalid - Marks input as invalid (shows error styling)
 * @attr {string} autocomplete - HTML autocomplete attribute
 * @attr {string} inputmode - HTML inputmode attribute for mobile keyboards
 * @attr {string} pattern - Validation pattern (regex)
 * @attr {number} maxlength - Maximum character length
 * @attr {number} minlength - Minimum character length
 * @attr {number} min - Minimum value (for numeric types)
 * @attr {number} max - Maximum value (for numeric types)
 * @attr {number} step - Step value (for numeric types)
 * @attr {boolean} clearable - Shows clear button when input has value
 * @attr {boolean} multiline - Renders a textarea instead of an input (multi-line mode)
 * @attr {string} type - Input type: "text" | "email" | "password" | "tel" | "url" | "search" | "number" | "textarea" (default: "text"); use "textarea" or the `multiline` attribute for multi-line mode
 * @attr {number} rows - Number of visible text rows in multiline mode (default: 4)
 * @attr {string} resize - Resize behavior in multiline mode: "none" | "vertical" | "horizontal" | "both" (default: "vertical")
 * @attr {string} aria-label - Accessible label
 * @attr {string} aria-labelledby - ID of labeling element
 * @attr {string} aria-describedby - ID of describing element
 * @attr {boolean} aria-invalid - ARIA invalid state
 * 
 * @slot prefix - Content before the input (icons, text)
 * @slot suffix - Content after the input (buttons, icons)
 * 
 * @fires input - Dispatched when input value changes (native event)
 * @fires change - Dispatched when input loses focus after value change (native event)
 * @fires ctl-clear - Dispatched when clear button is clicked
 * 
 * @cssprop --ctl-font - Font family (default: system-ui,-apple-system,Segoe UI,Roboto,Arial)
 * @cssprop --ctl-radius - Border radius (default: 10px)
 * @cssprop --ctl-color-text - Text color (default: #111827)
 * @cssprop --ctl-color-muted - Muted text color (default: #6b7280)
 * @cssprop --ctl-color-border - Border color (default: #e5e7eb)
 * @cssprop --ctl-color-surface - Background color (default: #ffffff)
 * @cssprop --ctl-color-focus - Focus ring color (default: #4f46e5)
 * @cssprop --ctl-color-danger - Error/invalid color (default: #dc2626)
 * @cssprop --ctl-field-height - Field height (default: 40px)
 * @cssprop --ctl-pad-x - Horizontal padding (default: 12px)
 * @cssprop --ctl-gap - Gap between slots (default: 8px)
 */
export class CtlTextField extends HTMLElement {
  /**
   * Observed HTML attributes that trigger attributeChangedCallback
   * @returns {string[]} Array of attribute names to observe
   */
  static get observedAttributes() {
    return [
      "name", "value", "type", "placeholder",
      "disabled", "required", "readonly", "invalid",
      "autocomplete", "inputmode", "pattern",
      "maxlength", "minlength", "min", "max", "step",
      "clearable",
      // multiline/textarea mode
      "multiline", "rows", "resize",
      // ARIA passthrough
      "aria-label", "aria-labelledby", "aria-describedby", "aria-invalid"
    ];
  }

  /**
   * Create a new CtlTextField instance
   * Initializes shadow DOM and internal element references
   */
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._input = this.shadowRoot.getElementById("input");
    this._textarea = this.shadowRoot.getElementById("textarea");
    this._clearBtn = this.shadowRoot.getElementById("clearBtn");

    this._onInput = this._onInput.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onClear = this._onClear.bind(this);
  }

  connectedCallback() {
    this._upgradeProperty("value");
    this._upgradeProperty("name");
    this._upgradeProperty("type");
    this._upgradeProperty("placeholder");

    if (!this.hasAttribute("type") && !this.hasAttribute("multiline")) {
      this.setAttribute("type", "text");
    }

    this._input.addEventListener("input", this._onInput);
    this._input.addEventListener("change", this._onChange);
    this._input.addEventListener("keydown", this._onKeyDown);
    this._textarea.addEventListener("input", this._onInput);
    this._textarea.addEventListener("change", this._onChange);
    this._textarea.addEventListener("keydown", this._onKeyDown);
    this._clearBtn.addEventListener("click", this._onClear);

    this._syncMultilineMode();
    this._syncToInput();
    this._syncHasValue();
  }

  disconnectedCallback() {
    this._input.removeEventListener("input", this._onInput);
    this._input.removeEventListener("change", this._onChange);
    this._input.removeEventListener("keydown", this._onKeyDown);
    this._textarea.removeEventListener("input", this._onInput);
    this._textarea.removeEventListener("change", this._onChange);
    this._textarea.removeEventListener("keydown", this._onKeyDown);
    this._clearBtn.removeEventListener("click", this._onClear);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    this._syncToInput(name);
    if (name === "value") this._syncHasValue();
    if (name === "disabled") this._syncDisabled();
    if (name === "readonly") this._syncReadonly();
    if (name === "required") this._syncRequired();
    if (name === "type") this._syncType();
    if (name === "invalid") this._syncInvalid();
    if (name === "multiline" || name === "type") {
      this._syncMultilineMode();
      // After switching modes the newly-active element must be fully hydrated
      this._syncToInput();
    }
    if (name === "rows") this._syncRows();
    if (name === "resize") this._syncResize();
  }

  // --- Properties (nice ergonomic API) ---
  get value() {
    return this.getAttribute("value") ?? "";
  }
  set value(v) {
    const next = toAttr(v);
    if (next === null) this.removeAttribute("value");
    else this.setAttribute("value", next);
  }

  get name() { return this.getAttribute("name"); }
  set name(v) { v == null ? this.removeAttribute("name") : this.setAttribute("name", String(v)); }

  get disabled() { return this.hasAttribute("disabled"); }
  set disabled(v) { v ? this.setAttribute("disabled", "") : this.removeAttribute("disabled"); }

  get required() { return this.hasAttribute("required"); }
  set required(v) { v ? this.setAttribute("required", "") : this.removeAttribute("required"); }

  get readOnly() { return this.hasAttribute("readonly"); }
  set readOnly(v) { v ? this.setAttribute("readonly", "") : this.removeAttribute("readonly"); }

  focus(options) { this._activeEl.focus(options); }
  blur() { this._activeEl.blur(); }
  select() { this._activeEl.select(); }

  // --- Internals ---
  _upgradeProperty(prop) {
    if (Object.prototype.hasOwnProperty.call(this, prop)) {
      const value = this[prop];
      delete this[prop];
      this[prop] = value;
    }
  }

  _syncToInput(changedName) {
    const active = this._activeEl;
    // Always keep active element's value in sync with attribute value unless user is typing.
    if (!changedName || changedName === "value") {
      const v = this.getAttribute("value");
      if (active.value !== (v ?? "")) active.value = v ?? "";
    }

    // Reflect attribute set to internal input
    for (const attr of REFLECT_TO_INPUT) {
      if (changedName && changedName !== attr) continue;
      if (this.hasAttribute(attr)) active.setAttribute(attr, this.getAttribute(attr));
      else active.removeAttribute(attr);
    }

    // Boolean attrs
    if (!changedName || changedName === "disabled") this._syncDisabled();
    if (!changedName || changedName === "readonly") this._syncReadonly();
    if (!changedName || changedName === "required") this._syncRequired();
    if (!changedName || changedName === "type") this._syncType();
    if (!changedName || changedName === "invalid") this._syncInvalid();
  }

  _syncDisabled() {
    const disabled = this.hasAttribute("disabled");
    this._input.disabled = disabled;
    this._textarea.disabled = disabled;
    // Note: host styles handle pointer events/opacity.
  }

  _syncReadonly() {
    const readonly = this.hasAttribute("readonly");
    this._input.readOnly = readonly;
    this._textarea.readOnly = readonly;
  }

  _syncRequired() {
    const required = this.hasAttribute("required");
    this._input.required = required;
    this._textarea.required = required;
    // ctl-form-field may set aria-required; we don't override it here.
  }

  _syncType() {
    const type = (this.getAttribute("type") || "text").toLowerCase();
    if (type === "textarea") {
      // type="textarea" is an alias for multiline mode; handled by _syncMultilineMode
      return;
    }
    // Keep types safe for enterprise use.
    const allowed = new Set(["text", "email", "password", "search", "url", "tel", "number"]);
    this._input.type = allowed.has(type) ? type : "text";
  }

  _syncInvalid() {
    // Prefer explicit aria-invalid passthrough if provided, otherwise follow [invalid]
    if (!this.hasAttribute("aria-invalid")) {
      const invalid = this.hasAttribute("invalid");
      if (invalid) {
        this._input.setAttribute("aria-invalid", "true");
        this._textarea.setAttribute("aria-invalid", "true");
      } else {
        this._input.removeAttribute("aria-invalid");
        this._textarea.removeAttribute("aria-invalid");
      }
    }
  }

  /** Returns true when component is in multiline (textarea) mode */
  get _isMultiline() {
    const typeAttr = this.getAttribute("type");
    return this.hasAttribute("multiline") || (typeAttr && typeAttr.toLowerCase() === "textarea");
  }

  /** Returns the currently active input element (input or textarea) */
  get _activeEl() {
    return this._isMultiline ? this._textarea : this._input;
  }

  _syncMultilineMode() {
    const ml = this._isMultiline;
    if (ml) {
      // Copy value when transitioning to multiline
      if (this._textarea.hidden) {
        this._textarea.value = this._input.value;
      }
      this._input.hidden = true;
      this._textarea.hidden = false;
    } else {
      // Copy value when transitioning to single-line
      if (this._input.hidden) {
        this._input.value = this._textarea.value;
      }
      this._textarea.hidden = true;
      this._input.hidden = false;
    }
    this._syncRows();
    this._syncResize();
  }

  _syncRows() {
    const attr = this.getAttribute("rows");
    let rows = parseInt(attr, 10);

    if (!Number.isFinite(rows)) {
      rows = 4;
    }

    if (rows < 1) {
      rows = 1;
    }
    this._textarea.rows = rows;
  }

  _syncResize() {
    const resize = this.getAttribute("resize") || "vertical";
    const allowed = new Set(["none", "vertical", "horizontal", "both"]);
    this._textarea.style.resize = allowed.has(resize) ? resize : "vertical";
  }

  _syncHasValue() {
    const hasValue = (this._activeEl.value || "").length > 0;
    if (hasValue) this.setAttribute("_hasvalue", "");
    else this.removeAttribute("_hasvalue");
  }

  _emit(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        composed: true,
        detail
      })
    );
  }

  _onInput(e) {
    const value = e.target.value;
    // Reflect live value to attribute so outside world stays in sync
    this.setAttribute("value", value);
    this._syncHasValue();

    this._emit("ctl-input", {
      name: this.getAttribute("name") || null,
      value,
      originalEvent: e
    });
  }

  _onChange(e) {
    const value = e.target.value;
    // change fires on commit; ensure attribute is synced
    this.setAttribute("value", value);
    this._syncHasValue();

    this._emit("ctl-change", {
      name: this.getAttribute("name") || null,
      value,
      originalEvent: e
    });
  }

  _onKeyDown(e) {
    // Clear on Escape when clearable and has value (common enterprise UX)
    if (e.key === "Escape" && this.hasAttribute("clearable") && this.hasAttribute("_hasvalue")) {
      e.preventDefault();
      this._clear();
    }
  }

  _onClear(e) {
    e.preventDefault();
    this._clear();
  }

  _clear() {
    if (this.hasAttribute("disabled") || this.hasAttribute("readonly")) return;

    this._input.value = "";
    this._textarea.value = "";
    this.setAttribute("value", "");
    this._syncHasValue();

    // Fire both: input (live) + change (commit) as many design systems do for clear
    this._emit("ctl-clear", { name: this.getAttribute("name") || null });
    this._emit("ctl-input", { name: this.getAttribute("name") || null, value: "", originalEvent: null });
    this._emit("ctl-change", { name: this.getAttribute("name") || null, value: "", originalEvent: null });

    // Keep focus in field after clearing
    this._activeEl.focus();
  }
}

customElements.define("ctl-text-field", CtlTextField);
